import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Tag, Space, DatePicker, Select, Checkbox } from '../../ui';
import type { TagTone } from '../../ui';
import dayjs, { Dayjs } from 'dayjs';
import { disableFuture } from '../../lib/dateRange';
import { request } from '../../api/client';
import { TwDataTable as DataTable, DataTableColumn } from '../../components/TwDataTable';
import { formatIstTime } from '../../lib/time';

const STATUS_COLOR: Record<string, TagTone> = {
  PRESENT: 'green', HALF_DAY: 'gold', LEAVE: 'blue', ABSENT: 'red', HOLIDAY: 'neutral',
};
const SOURCE_TAG: Record<string, { text: string; tone: TagTone }> = {
  REGULARIZED: { text: 'Regularized', tone: 'purple' },
  MANUAL: { text: 'Manual', tone: 'blue' },
};
const ALL_STATUSES = ['PRESENT', 'HALF_DAY', 'LEAVE', 'ABSENT', 'HOLIDAY'];
const fmtWorked = (m?: number | null) => (m == null ? '—' : `${Math.floor(m / 60)}h ${m % 60}m`);

/** The signed-in staff member's own attendance history, with filters. Default shows only
 *  "active" days (Present / Half-day); a checkbox reveals the full history (Leave/Absent/Holiday). */
export function MyAttendanceHistoryCard() {
  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().subtract(30, 'day'), dayjs()]);
  const [status, setStatus] = useState<string | undefined>();
  const [source, setSource] = useState<string | undefined>();
  const [activeOnly, setActiveOnly] = useState(true);

  const { data, isLoading } = useQuery({
    queryKey: ['my-attendance', range[0].format('YYYY-MM-DD'), range[1].format('YYYY-MM-DD')],
    queryFn: () => request<any[]>({
      url: '/attendance/me/history',
      params: { from: range[0].format('YYYY-MM-DD'), to: range[1].format('YYYY-MM-DD') },
    }),
  });

  const rows = useMemo(() => {
    const records = (data ?? []) as any[];
    if (activeOnly) {
      // Only worked days (the backend already returns newest-first).
      let r = records.filter((x) => x.status === 'PRESENT' || x.status === 'HALF_DAY');
      if (source) r = r.filter((x) => x.source === source);
      return r;
    }
    // Full history: walk every day in the range; fill PAST days with no record as Absent
    // (today / future are never marked absent — the day isn't over).
    const byDate = new Map(records.map((x) => [x.logDate, x]));
    const today = dayjs();
    const end = range[1].isAfter(today, 'day') ? today : range[1];
    const full: any[] = [];
    for (let d = end; !d.isBefore(range[0], 'day'); d = d.subtract(1, 'day')) {
      const ds = d.format('YYYY-MM-DD');
      const rec = byDate.get(ds);
      if (rec) full.push(rec);
      else if (d.isBefore(today, 'day')) full.push({ id: `gap-${ds}`, logDate: ds, status: 'ABSENT', synthetic: true });
    }
    let r = full;
    if (status) r = r.filter((x) => x.status === status);
    if (source) r = r.filter((x) => x.source === source);
    return r;
  }, [data, range, activeOnly, status, source]);

  const summary = useMemo(() => {
    const records = (data ?? []) as any[];
    const present = records.filter((x) => x.status === 'PRESENT').length;
    const half = records.filter((x) => x.status === 'HALF_DAY').length;
    const workedMin = records.reduce((s: number, x) => s + (x.workedMin ?? 0), 0);
    // Absent = past days in the range with no record (+ any recorded ABSENT).
    const have = new Set(records.map((x) => x.logDate));
    const today = dayjs();
    const end = range[1].isAfter(today, 'day') ? today : range[1];
    let absent = records.filter((x) => x.status === 'ABSENT').length;
    for (let d = end; !d.isBefore(range[0], 'day'); d = d.subtract(1, 'day')) {
      if (d.isBefore(today, 'day') && !have.has(d.format('YYYY-MM-DD'))) absent++;
    }
    return { present, half, absent, workedMin };
  }, [data, range]);

  const columns: DataTableColumn<any>[] = [
    { key: 'logDate', title: 'Date', dataIndex: 'logDate', sortable: true, alwaysVisible: true, width: 120 },
    { key: 'checkInAt', title: 'Check-in', dataIndex: 'checkInAt', sortable: true, render: (v: string) => (v ? formatIstTime(v) : '—') },
    { key: 'checkOutAt', title: 'Check-out', dataIndex: 'checkOutAt', render: (v: string, r: any) => (v ? formatIstTime(v) : (r.checkInAt ? <Tag tone="blue">Open</Tag> : '—')) },
    { key: 'workedMin', title: 'Worked', dataIndex: 'workedMin', align: 'right' as const, sortable: true, render: (v: number) => fmtWorked(v) },
    {
      key: 'status', title: 'Status', dataIndex: 'status', sortable: true, alwaysVisible: true,
      render: (v: string, r: any) => (
        <Space size={4} wrap>
          <Tag tone={STATUS_COLOR[v] ?? 'neutral'}>{v}</Tag>
          {r.source && SOURCE_TAG[r.source] && <Tag tone={SOURCE_TAG[r.source].tone}>{SOURCE_TAG[r.source].text}</Tag>}
          {r.missingCheckout && <Tag tone="gold">Missing check-out</Tag>}
        </Space>
      ),
    },
  ];

  return (
    <Card title="Attendance history">
      <Space wrap size="large" style={{ marginBottom: 10 }}>
        <span>Present: <b style={{ color: '#1F8A4C' }}>{summary.present}</b></span>
        <span>Half-day: <b style={{ color: '#E7B22C' }}>{summary.half}</b></span>
        <span>Absent: <b style={{ color: '#D9363E' }}>{summary.absent}</b></span>
        <span>Worked: <b>{fmtWorked(summary.workedMin)}</b></span>
      </Space>
      <DataTable
        storageKey="my-attendance-history"
        rowKey="id"
        loading={isLoading}
        dataSource={rows}
        columns={columns}
        pagination={{ pageSize: 10, showSizeChanger: true }}
        toolbarLeft={
          <>
            <DatePicker.RangePicker disabledDate={disableFuture} value={range} onChange={(r) => r && r[0] && r[1] && setRange([r[0], r[1]])} allowClear={false} />
            <Select placeholder="Status" allowClear disabled={activeOnly} className="w-35" value={status} onChange={setStatus}
              options={ALL_STATUSES.map((s) => ({ value: s, label: s }))} />
            <Select placeholder="Source" allowClear className="w-37.5" value={source} onChange={setSource}
              options={[{ value: 'SELF', label: 'Self' }, { value: 'REGULARIZED', label: 'Regularized' }, { value: 'MANUAL', label: 'Manual' }]} />
            <Checkbox checked={activeOnly} onCheckedChange={(checked) => { setActiveOnly(checked); if (checked) setStatus(undefined); }} label="Active only" />
          </>
        }
      />
    </Card>
  );
}
